import React, { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import InvisiqueLogo from "@/components/site/Logo";
import PhoneInput10 from "@/components/site/PhoneInput10";
import DemoRequestDialog from "@/components/site/DemoRequestDialog";
import { toE164, to10Digit, isValid10Digit } from "@/lib/phone";
import { toast } from "sonner";
import { Phone, ArrowLeft, ArrowRight, Stethoscope, Crown, Headset, User, WarningCircle, Calendar, FilePlus, Scan, Pulse, Quotes, ChatCircleText, Stack, Smiley, Sparkle, ShieldCheck } from "@phosphor-icons/react";

/* Per-role copy. iter98.85 — replaced the generic "Welcome back, …"
 * line with confident, on-brand titles that follow a single pattern:
 * "Your [domain] is ready." (B2B portals) / "Your aligner journey
 * awaits." (patient). Reads premium without the saccharine bounce of
 * "Welcome back!" — sets the tone for a serious medical platform. */
const ROLE_META = {
    "/admin-login": {
        key: "admin",
        Icon: Crown,
        overline: "Admin login",
        title: "Your command center is ready.",
        helper: "Sign in with your registered admin phone number to access the console.",
    },
    "/doctor-login": {
        key: "doctor",
        Icon: Stethoscope,
        overline: "Provider login",
        // iter100.82 — Title + helper removed. The stethoscope disc +
        // "PROVIDER LOGIN" overline carry all the context a returning
        // doctor needs; the extra two lines were pure ceremony that
        // pushed the phone input further down on laptop screens.
        title: "",
        helper: "",
    },
    "/coe-login": {
        key: "coe",
        Icon: Headset,
        overline: "COE login",
        title: "Your planning queue is ready.",
        helper: "Sign in with the phone number your admin registered for you.",
    },
    "/sales-login": {
        key: "sales",
        Icon: Headset,
        overline: "Sales login",
        title: "Your sales pipeline is ready.",
        helper: "Sign in with the phone number your manager registered for you.",
    },
    "/patient-login": {
        key: "patient",
        Icon: User,
        overline: "Patient portal",
        // iter100.87 — Title + helper removed; the split-bento hero
        // to the left carries all the context (journey timeline +
        // patient stories). Right-pane keeps overline + phone input.
        title: "",
        helper: "",
    },
    "/login": {
        key: "patient",
        Icon: User,
        overline: "Patient portal",
        title: "",
        helper: "",
    },
};

/* iter100.85 — Rotating doctor testimonials for the /doctor-login
   left pane. Cycles every 8s. Placeholder copy below — swap the
   `quote` / `name` / `practice` / `city` fields with real doctor
   verbatims once collected. Keep it under ~120 chars per quote so
   the card doesn't grow vertically and push the "Join 500+" rail
   or the OTP card off-screen on laptop viewports. */
const DOCTOR_TESTIMONIALS = [
    {
        quote: "Cut my case-planning time in half. The 3D scan review is a genuine game-changer.",
        name: "Dr. Priya Sharma",
        practice: "Sharma Dental",
        city: "Mumbai",
        initial: "P",
        tone: "primary",
    },
    {
        quote: "Compliance tracking is exceptional. Patients love the transparency, and my staff no longer chases wear-time updates.",
        name: "Dr. Rajesh Kumar",
        practice: "Kumar Orthodontics",
        city: "Bengaluru",
        initial: "R",
        tone: "amber",
    },
    {
        quote: "Onboarding was smooth — my team was live in a day. Support responds like they're in the next room.",
        name: "Dr. Anjali Mehta",
        practice: "Mehta Smile Studio",
        city: "Delhi",
        initial: "A",
        tone: "emerald",
    },
    {
        quote: "Best clinical control I've had in 15 years of practice. The staging approvals are transparent and fast.",
        name: "Dr. Vikram Rao",
        practice: "Rao Orthodontic Clinic",
        city: "Hyderabad",
        initial: "V",
        tone: "primary",
    },
];

const TESTIMONIAL_TONES = {
    primary: { bg: "bg-primary/10", ring: "ring-primary/20", text: "text-primary" },
    amber:   { bg: "bg-amber-400/20", ring: "ring-amber-400/30", text: "text-amber-700" },
    emerald: { bg: "bg-emerald-500/10", ring: "ring-emerald-500/20", text: "text-emerald-700" },
};

/* iter100.87 — Patient portal login redesign. Left pane gets a
   4-step journey timeline (Consult → Scan → Aligners → New Smile)
   above a rotating before/after story reel. Copy tone is
   emotional + aspirational (unlike doctor login which is
   clinical + credentialed) because the audience is a patient
   coming back to check their treatment, not a doctor arriving
   for work. */
const PATIENT_JOURNEY = [
    { icon: ChatCircleText, label: "Consult",   sub: "Online" },
    { icon: Scan,           label: "3D Scan",   sub: "At clinic" },
    { icon: Stack,          label: "Aligners",  sub: "Delivered" },
    { icon: Smiley,         label: "New Smile", sub: "Yours" },
];

export default function Login() {
    const navigate = useNavigate();
    const location = useLocation();
    const { login, setUser } = useAuth();
    const [step, setStep] = useState(1);
    // Phone may arrive pre-filled via location.state.prefillPhone when the
    // user clicked "Go to X Portal" on a wrong-portal banner. Strip the
    // +91 prefix since the UI standardises on 10-digit display.
    const [phone, setPhone] = useState(to10Digit(location.state?.prefillPhone || ""));
    const [code, setCode] = useState("");
    // 2026-02-28 — `name` field removed from login form. Profile completion
    // happens just-in-time on the Patient Dashboard when a fresh patient
    // has no name on file. Splits auth from profile cleanly.
    const [loading, setLoading] = useState(false);
    const [devCode, setDevCode] = useState(null);
    // 2026-02-28 — privacy upgrade: backend no longer reveals which portal a
    // phone belongs to. Wrong-portal / unregistered-phone failures all
    // collapse to a single generic "Sign-in failed" inline error. No more
    // amber "This phone uses the Admin Portal" banner — that leaked role
    // info to anyone who tried random phone numbers.
    const [signinError, setSigninError] = useState(null);
    // iter100.85 — Rotating testimonial index for the doctor login
    // left pane. 8s per quote. Only advances when the doctor role is
    // active (the left pane isn't rendered for other roles anyway,
    // so the interval is a no-op there but we keep the guard for
    // future-proofing / bundle-size clarity).
    const [testimonialIdx, setTestimonialIdx] = useState(0);
    // 2026-02-29 iter98.62 — Doctor-Portal onboarding nudge. When a doctor
    // lands on /doctor-login without an account yet, we surface two
    // discoverability CTAs ("Talk to sales" · "Apply directly") under the
    // OTP form so they don't bounce off the phone-only wall. Sales-led
    // path opens the existing DemoRequestDialog inline (posts to
    // /api/b2b-leads → auto-routes by territory); self-serve path
    // navigates to /for-doctors#apply where the full ApplyWizard lives.
    const [demoOpen, setDemoOpen] = useState(false);

    const redirectTo = location.state?.from || null;
    const role = ROLE_META[location.pathname] || ROLE_META["/login"];
    const { Icon, overline, title, helper, key: roleKey } = role;

    // iter100.85 — Rotate the doctor testimonial every 8 seconds.
    // Only runs on /doctor-login (roleKey === "doctor"). Cleaned up
    // on unmount so navigating away doesn't leak the interval.
    useEffect(() => {
        if (roleKey !== "doctor") return undefined;
        const id = window.setInterval(() => {
            setTestimonialIdx((i) => (i + 1) % DOCTOR_TESTIMONIALS.length);
        }, 8000);
        return () => window.clearInterval(id);
    }, [roleKey]);

    const sendOtp = async (e) => {
        e?.preventDefault();
        if (!isValid10Digit(phone)) {
            toast.error("Enter your 10-digit mobile number");
            return;
        }
        setLoading(true);
        try {
            // Pass the intended portal role to send-otp so the backend can
            // silently suppress OTP sends for wrong-portal phones (privacy).
            const intendedRole = roleKey === "coe" ? "staff" : roleKey;
            const { data } = await api.post("/auth/send-otp", { phone: toE164(phone), role: intendedRole });
            setStep(2);
            if (data.dev_code) setDevCode(data.dev_code);
            toast.success("OTP sent");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to send OTP");
        }
        setLoading(false);
    };

    const verifyOtp = async (e) => {
        e?.preventDefault();
        if (code.length !== 6) return;
        setLoading(true);
        try {
            // Tell the backend WHICH login page we came from. Backend uses
            // this for TWO things now:
            //   1. Gate new signups (patient is the only role that can auto-create)
            //   2. Reject existing users whose actual role doesn't match the
            //      portal they're trying to sign in via (2026-02-28 portal gating fix)
            const intendedRole = roleKey === "coe" ? "staff" : roleKey;

            /* iter98.39 — When the user arrived here from the public
               booking flow (`?intent=book&b_name=...&b_email=...`),
               surface their captured identity to verify-otp UP FRONT so
               the freshly-created user record carries the name from
               second one. Without this, the user lands in the portal
               with name="" — sidebar shows "User", header shows just
               the phone (per 2026-06-12 bug report). The backend has
               accepted optional `name`/`email` on verify-otp since
               iter98.37 (see test_iter98_37_public_booking_flow.py). */
            const qpPre = new URLSearchParams(location.search);
            const bookingIntent = qpPre.get("intent") === "book";
            const capturedName = bookingIntent ? (qpPre.get("b_name") || "").trim() : "";
            const capturedEmail = bookingIntent ? (qpPre.get("b_email") || "").trim() : "";

            const verifyPayload = { phone: toE164(phone), code, role: intendedRole };
            if (capturedName) verifyPayload.name = capturedName;
            if (capturedEmail) verifyPayload.email = capturedEmail;

            const { data } = await api.post("/auth/verify-otp", verifyPayload);
            login(data.token, data.user);
            toast.success(`Welcome${data.user.name ? ", " + data.user.name : ""}!`);

            // Stash a small "last user" hint so the next visit to a `*-login`
            // route can show a personalised "Welcome back, …" line inside
            // the 1.2s portal landing splash. Name only — no token, no PII
            // beyond what the user already shares freely on the dashboard.
            try {
                // Strip any leading title the user already has on file
                // ("Dr. James Wilson" → "James"; "Ms. Aanya" → "Aanya") so
                // we don't end up with "Welcome back, Dr. Dr. James".
                const rawName = (data.user.name || "").trim();
                const stripped = rawName.replace(/^(dr|mr|mrs|ms|miss|prof)\.?\s+/i, "");
                const firstName = stripped.split(/\s+/)[0] || "";
                const greet = firstName
                    ? (data.user.role === "doctor" ? `Dr. ${firstName}` : firstName)
                    : "";
                if (greet) {
                    localStorage.setItem("invisique_last_user", JSON.stringify({
                        role: data.user.role,
                        greet,
                        ts: Date.now(),
                    }));
                }
            } catch { /* localStorage may be unavailable in private mode — splash falls back to default tagline */ }

            /* iter98.37 — Public-booking intent finisher.
               When a patient signs in via the cross-host booking flow
               (clicking "Confirm booking" on the marketing /book/:id
               page), the redirect URL carries `intent=book` plus
               `b_*` params describing the appointment they wanted to
               book. Finishing this inline — instead of bouncing them
               to the patient dashboard — preserves their selection
               across the OTP hop (portal hostname doesn't have the
               /book route). Same code path works for same-host preview
               where the marketing site and portal share an origin. */
            try {
                const qp = new URLSearchParams(location.search);
                if (qp.get("intent") === "book" && data.user.role === "patient") {
                    const docId = qp.get("b_doctor");
                    const apptDate = qp.get("b_date");
                    const apptTime = qp.get("b_time");
                    const apptNotes = qp.get("b_notes") || "";
                    // capturedName / capturedEmail already in scope from
                    // the pre-verify block above — reused here.
                    if (docId && apptDate && apptTime) {
                        const headers = { Authorization: `Bearer ${data.token}` };
                        // Defence-in-depth: verify-otp already set the name
                        // via the captured payload, but if the user pre-
                        // existed in the DB with an empty name field, the
                        // backend returns their old record (name="") and
                        // does NOT auto-overwrite. Backfill via PUT and
                        // setUser to refresh the in-memory context so the
                        // sidebar + header don't keep showing "User" /
                        // the phone number (per 2026-06-12 bug report).
                        if (!data.user.name && capturedName) {
                            try {
                                const r = await api.put("/auth/profile",
                                    { name: capturedName, email: capturedEmail || undefined },
                                    { headers }
                                );
                                if (r?.data && setUser) setUser(r.data);
                            } catch { /* non-fatal; appointment proceeds */ }
                        }
                        // 2. Fire the appointment with the captured slot + notes.
                        await api.post("/appointments", {
                            doctor_id: docId,
                            date: apptDate,
                            time: apptTime,
                            type: "consultation",
                            notes: apptNotes,
                        }, { headers });
                        toast.success("Appointment confirmed!");
                        // Clear any cross-host sessionStorage draft so the
                        // patient doesn't see "Finishing your booking…" on
                        // a later visit.
                        try { window.sessionStorage.removeItem("invisique_pending_booking"); } catch { /* noop */ }
                        navigate("/patient/appointments");
                        return;
                    }
                }
            } catch (bookErr) {
                // Booking finisher failed (slot taken, etc.). Surface the
                // detail and continue to the normal post-login nav so the
                // user lands somewhere sensible.
                toast.error(bookErr.response?.data?.detail || "Could not finish your booking. Please try again from your dashboard.");
            }

            if (redirectTo) navigate(redirectTo);
            else if (data.user.role === "admin") navigate("/admin");
            else if (data.user.role === "doctor") navigate("/doctor");
            else if (data.user.role === "clinic_staff") navigate("/doctor");
            // iter98.52 — Sales Portal lands here. Both reps and managers
            // use the same /sales surface; manager privileges are derived
            // from `user.role` server-side.
            else if (data.user.role === "sales" || data.user.role === "sales_manager") navigate("/sales");
            else if (data.user.role === "staff") {
                // Send each COE specialist straight to their working surface,
                // so they don't have to click through a lobby Dashboard tile.
                const t = data.user.staff_type;
                if (t === "planner") navigate("/coe");
                else if (t === "qa") navigate("/coe/qa");
                else if (t === "production") navigate("/coe/production");
                else if (t === "technician") navigate("/coe/technician");
                else if (t === "sales") navigate("/coe/sales");
                else if (t === "support") navigate("/coe/support");
                else navigate("/coe");
            }
            else navigate("/patient");
        } catch (err) {
            // 2026-02-28 — generic non-disclosing error UX.
            // Backend returns 401 with the same generic "Sign-in failed."
            // message for: wrong portal, unregistered phone on an invite-
            // only portal, and OTP mismatch on a wrong-portal phone (the
            // OTP was silently never sent). Render the detail inline so
            // the user sees it next to the OTP input instead of a
            // floating red toast — calmer + accessible to screen readers
            // via the alert role.
            const detail = err.response?.data?.detail || "Sign-in failed. Please try again.";
            setSigninError(detail);
        }
        setLoading(false);
    };

    // Clear the inline error whenever the user re-engages (goes back to
    // edit phone, or starts typing a fresh OTP). Same UX pattern as the
    // old portalMismatch — never trap a stale error on screen.
    useEffect(() => {
        if (step === 1) setSigninError(null);
    }, [step]);
    useEffect(() => {
        if (signinError && code.length === 0) setSigninError(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [code]);

    return (
        <div className="min-h-[calc(100vh-4rem)] relative bg-secondary/30 flex items-center justify-center px-5 py-6 sm:py-8 sm:px-8 overflow-hidden" data-testid="login-page">
            {/* Faint blue-grid background — same texture as the Landing hero, just
                enough visual interest on wide monitors without fighting the form. */}
            <div className="absolute inset-0 blue-grid-bg opacity-50 mask-fade-b pointer-events-none" aria-hidden="true" />
            {/* Two soft halos top + bottom, mirroring the marketing hero treatment. */}
            <div className="absolute -top-32 -left-20 w-80 h-80 rounded-full bg-primary/10 blur-3xl pointer-events-none" aria-hidden="true" />
            <div className="absolute -bottom-32 -right-20 w-80 h-80 rounded-full bg-amber-200/30 blur-3xl pointer-events-none" aria-hidden="true" />

            {/* iter100.83 — Split-bento layout for /doctor-login ONLY. The
                left pane sells the platform (premium overline, headline,
                two bento feature cards, doctor-count social-proof rail)
                while the right pane holds the OTP card. Other roles keep
                the plain centered card via the ternary below. */}
            <div className={`relative w-full ${(roleKey === "doctor" || roleKey === "patient") ? "max-w-5xl grid lg:grid-cols-12 gap-8 lg:gap-14 items-center" : "max-w-md"}`}>

                {roleKey === "doctor" && (
                    <div className="hidden lg:flex lg:col-span-7 flex-col relative pr-4 xl:pr-8 animate-in fade-in slide-in-from-left-8 duration-700">
                        <div className="absolute top-1/4 -left-10 w-72 h-72 bg-primary/10 rounded-full blur-[80px] pointer-events-none" />
                        <div className="absolute bottom-1/4 right-0 w-64 h-64 bg-amber-400/10 rounded-full blur-[80px] pointer-events-none" />

                        <div className="relative z-10">
                            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-border/70 shadow-sm text-primary text-[10px] font-bold tracking-[0.2em] uppercase mb-6" data-testid="login-hero-eyebrow">
                                <Crown size={13} weight="fill" className="text-amber-500" /> Invisique™ Provider Portal
                            </div>

                            <h2 className="font-display text-4xl xl:text-5xl font-bold tracking-tight text-foreground leading-[1.15]" data-testid="login-hero-headline">
                                Precision planning.<br />
                                <span className="text-primary">Exceptional results.</span>
                            </h2>

                            <p className="text-muted-foreground mt-5 text-base max-w-md leading-relaxed">
                                Manage your aligner cases with absolute clinical control. Review 3D scans, approve staging, and track patient compliance seamlessly.
                            </p>

                            <div className="grid grid-cols-2 gap-4 mt-8 max-w-md">
                                <div className="p-4 rounded-2xl bg-white border border-border/60 bento-shadow relative overflow-hidden group" data-testid="login-hero-feature-staging">
                                    <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                    <div className="w-9 h-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-3 ring-1 ring-primary/20 group-hover:scale-110 transition-transform duration-300">
                                        <Scan size={18} weight="duotone" />
                                    </div>
                                    <div className="font-display text-lg font-bold text-foreground leading-tight">Smart Staging</div>
                                    <div className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider mt-1">COE-assisted planning</div>
                                </div>

                                <div className="p-4 rounded-2xl bg-gradient-to-br from-amber-50/50 to-amber-100/20 border border-amber-200/50 bento-shadow relative overflow-hidden group" data-testid="login-hero-feature-monitoring">
                                    <div className="absolute inset-0 bg-gradient-to-br from-amber-400/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                    <div className="w-9 h-9 rounded-xl bg-amber-400/20 text-amber-600 flex items-center justify-center mb-3 ring-1 ring-amber-400/30 group-hover:scale-110 transition-transform duration-300">
                                        <Pulse size={18} weight="duotone" />
                                    </div>
                                    <div className="font-display text-lg font-bold text-foreground leading-tight">Live Monitoring</div>
                                    <div className="text-[10px] text-amber-800/70 font-semibold uppercase tracking-wider mt-1">Wear-time compliance</div>
                                </div>
                            </div>

                            {/* iter100.85 — Rotating doctor testimonial. Auto-
                                advances every 8s; the `key={testimonialIdx}`
                                on the inner content re-triggers the
                                fade+slide animation on each rotation so the
                                change is visible without being noisy. */}
                            {(() => {
                                const t = DOCTOR_TESTIMONIALS[testimonialIdx];
                                const tone = TESTIMONIAL_TONES[t.tone] || TESTIMONIAL_TONES.primary;
                                return (
                                    <div
                                        className="mt-8 border-t border-border/60 pt-5 max-w-md"
                                        data-testid="login-hero-testimonial"
                                    >
                                        <div
                                            key={testimonialIdx}
                                            className="relative animate-in fade-in slide-in-from-bottom-2 duration-500"
                                        >
                                            <Quotes
                                                size={22}
                                                weight="fill"
                                                className="text-primary/25 absolute -left-1 -top-2 pointer-events-none"
                                                aria-hidden="true"
                                            />
                                            <p
                                                className="text-sm text-foreground/85 leading-relaxed pl-6 italic"
                                                data-testid="login-hero-testimonial-quote"
                                            >
                                                {t.quote}
                                            </p>
                                            <div className="flex items-center gap-3 mt-4 pl-6">
                                                <div
                                                    className={`w-9 h-9 rounded-full ${tone.bg} border-2 border-background grid place-items-center text-sm font-bold ${tone.text} ring-2 ${tone.ring} shrink-0`}
                                                    aria-hidden="true"
                                                >
                                                    {t.initial}
                                                </div>
                                                <div className="min-w-0" data-testid="login-hero-testimonial-author">
                                                    <div className="text-xs font-bold text-foreground truncate">
                                                        {t.name}
                                                    </div>
                                                    <div className="text-[11px] text-muted-foreground truncate">
                                                        {t.practice} · {t.city}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Pagination dots — clickable so a doctor
                                            can pin the testimonial they're reading
                                            if they need more than 8s. Live region
                                            for screen-readers. */}
                                        <div
                                            className="flex items-center gap-1.5 mt-5 pl-6"
                                            role="tablist"
                                            aria-label="Doctor testimonials"
                                            data-testid="login-hero-testimonial-dots"
                                        >
                                            {DOCTOR_TESTIMONIALS.map((_, i) => (
                                                <button
                                                    key={i}
                                                    type="button"
                                                    role="tab"
                                                    aria-selected={i === testimonialIdx}
                                                    aria-label={`Testimonial ${i + 1} of ${DOCTOR_TESTIMONIALS.length}`}
                                                    onClick={() => setTestimonialIdx(i)}
                                                    className={`h-1.5 rounded-full transition-all ${
                                                        i === testimonialIdx
                                                            ? "w-6 bg-primary"
                                                            : "w-1.5 bg-border hover:bg-muted-foreground/40"
                                                    }`}
                                                    data-testid={`login-hero-testimonial-dot-${i}`}
                                                />
                                            ))}
                                            <span className="ml-auto text-[10px] text-muted-foreground font-medium">
                                                Join <span className="font-bold text-foreground">500+</span> orthodontists
                                            </span>
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                )}

                {/* iter100.87 — Patient portal hero: 4-step journey
                    timeline (Consult → Scan → Aligners → New Smile)
                    above a rotating before/after story reel. Copy
                    tone is emotional + aspirational; visuals lean on
                    typography (before → after pills, duration stamp)
                    rather than photos so we don't need approved
                    transformation imagery to ship. */}
                {roleKey === "patient" && (
                    <div className="hidden lg:flex lg:col-span-7 flex-col relative pr-4 xl:pr-8 animate-in fade-in slide-in-from-left-8 duration-700">
                        <div className="absolute top-1/4 -left-10 w-72 h-72 bg-primary/10 rounded-full blur-[80px] pointer-events-none" />
                        <div className="absolute bottom-1/4 right-0 w-64 h-64 bg-emerald-400/10 rounded-full blur-[80px] pointer-events-none" />

                        <div className="relative z-10">
                            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-border/70 shadow-sm text-primary text-[10px] font-bold tracking-[0.2em] uppercase mb-5" data-testid="login-hero-eyebrow">
                                <Sparkle size={13} weight="fill" className="text-amber-500" /> Invisique™ Patient Portal
                            </div>

                            <h2 className="font-display text-4xl xl:text-5xl font-bold tracking-tight text-foreground leading-[1.15]" data-testid="login-hero-headline">
                                Invisible Precision.<br />
                                <span className="text-primary">Uniquely Yours.</span>
                            </h2>

                            <p className="text-muted-foreground mt-4 text-base max-w-md leading-relaxed">
                                Track your trays, message your doctor, and pick up right where you left off — every step of your smile journey lives in one place.
                            </p>

                            {/* JOURNEY TIMELINE — 4 stops, connecting dashed
                                rail, subtle hover raise. Fully static (no
                                "you are here" pin) because at login we
                                don't know the patient's stage yet — we
                                show the journey as an aspirational map. */}
                            <div className="relative mt-8 max-w-md" data-testid="login-hero-journey">
                                {/* Dashed connecting line */}
                                <div className="absolute top-6 left-6 right-6 border-t-2 border-dashed border-primary/25 pointer-events-none" aria-hidden="true" />
                                <div className="relative grid grid-cols-4 gap-2">
                                    {PATIENT_JOURNEY.map((s, i) => {
                                        const StepIcon = s.icon;
                                        return (
                                            <div
                                                key={i}
                                                className="flex flex-col items-center text-center group"
                                                data-testid={`login-hero-journey-step-${i}`}
                                            >
                                                <div className="relative w-12 h-12 rounded-full bg-white border-2 border-primary/30 grid place-items-center shadow-sm group-hover:scale-110 group-hover:border-primary group-hover:bg-primary/5 transition-all duration-300">
                                                    <StepIcon size={20} weight="duotone" className="text-primary" />
                                                    <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-primary text-white text-[9px] font-bold grid place-items-center leading-none">
                                                        {i + 1}
                                                    </span>
                                                </div>
                                                <div className="text-[11px] font-bold text-foreground mt-2 leading-tight">{s.label}</div>
                                                <div className="text-[9px] text-muted-foreground uppercase tracking-wider font-semibold mt-0.5">{s.sub}</div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* iter100.88 — Rotating patient story reel
                                removed. `/patient-login` is entered by
                                RETURNING patients whose treatment has
                                already begun — they don't need
                                marketing testimonials. A single trust
                                microcopy line below the journey
                                timeline preserves the brand anchor
                                without cluttering the returning-user
                                experience. */}
                            <div
                                className="mt-8 flex items-center gap-2 text-[11px] text-muted-foreground font-medium max-w-md"
                                data-testid="login-hero-trustline"
                            >
                                <ShieldCheck size={14} weight="duotone" className="text-primary shrink-0" />
                                Your smile, engineered by <span className="font-bold text-foreground">Invisique&trade;</span>.
                            </div>
                        </div>
                    </div>
                )}

                <div className={(roleKey === "doctor" || roleKey === "patient") ? "lg:col-span-5 w-full max-w-md mx-auto relative z-10" : "w-full max-w-md mx-auto relative z-10"}>
                {/* Back-to-home rail, sits above the card */}
                <Link
                    to="/"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-primary mb-3 transition-colors"
                    data-testid="back-home-link"
                >
                    <ArrowLeft size={13} weight="bold" /> Back to home
                </Link>

                {/* CARD */}
                <div
                    className={
                        (roleKey === "doctor" || roleKey === "patient")
                        ? "relative bg-white/80 backdrop-blur-2xl rounded-3xl border border-white/60 shadow-[0_8px_32px_rgba(0,45,57,0.08)] p-6 sm:p-8 overflow-hidden animate-in fade-in zoom-in-95 duration-500"
                        : "relative bg-card rounded-3xl border border-border/70 bento-shadow p-5 sm:p-7"
                    }
                    data-testid="login-card"
                >
                    {(roleKey === "doctor" || roleKey === "patient") && (
                        <div className="absolute inset-0 bg-gradient-to-tr from-white/40 via-transparent to-white/10 pointer-events-none" aria-hidden="true" />
                    )}

                    {/* Brand monogram — anchors the card with the site-wide Invisique
                        logo mark (same `+` sparkle disc used by the Header). */}
                    <div className="flex flex-col items-center text-center mb-5 relative z-10">
                        <InvisiqueLogo size="sm" />
                    </div>

                    {step === 1 && (
                        <form onSubmit={sendOtp} className="space-y-4 relative z-10 animate-in fade-in slide-in-from-bottom-2 duration-500" data-testid="phone-form">
                            {roleKey === "doctor" ? (
                                <div className="flex flex-col items-center mb-4">
                                    <div className="relative w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-[#003E50] flex items-center justify-center shadow-lg shadow-primary/20 border border-primary/20 mb-3 group hover:scale-105 transition-transform duration-300">
                                        <Stethoscope size={24} weight="duotone" className="text-white drop-shadow-md" />
                                        <div className="absolute inset-0 rounded-2xl bg-primary/20 animate-ping opacity-20 pointer-events-none" style={{ animationDuration: '3s' }} />
                                    </div>
                                    <div className="inline-flex items-center gap-2">
                                        <div className="w-6 h-px bg-border" />
                                        <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-primary">{overline}</span>
                                        <div className="w-6 h-px bg-border" />
                                    </div>
                                </div>
                            ) : roleKey === "patient" ? (
                                /* iter100.87 — Patient portal right-pane disc.
                                   Same visual weight as the doctor's stethoscope
                                   disc but with a warmer teal→emerald gradient
                                   and a smiley icon that mirrors the "New Smile"
                                   step in the left-pane journey timeline. */
                                <div className="flex flex-col items-center mb-4">
                                    <div className="relative w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-emerald-600 flex items-center justify-center shadow-lg shadow-primary/20 border border-primary/20 mb-3 group hover:scale-105 transition-transform duration-300">
                                        <Smiley size={26} weight="duotone" className="text-white drop-shadow-md" />
                                        <div className="absolute inset-0 rounded-2xl bg-primary/20 animate-ping opacity-20 pointer-events-none" style={{ animationDuration: '3s' }} />
                                    </div>
                                    <div className="inline-flex items-center gap-2">
                                        <div className="w-6 h-px bg-border" />
                                        <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-primary">{overline}</span>
                                        <div className="w-6 h-px bg-border" />
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center">
                                    {/* Role-themed circular icon disc — matches the "rhythm of
                                        circles" pattern used across marketing pages. */}
                                    <div className="mx-auto w-10 h-10 rounded-full bg-primary text-white grid place-items-center ring-4 ring-secondary/40 mb-3" data-testid="login-role-icon">
                                        <Icon size={18} weight="fill" />
                                    </div>
                                    <div className="overline text-primary mb-1">{overline}</div>
                                    {title && (
                                        <h1 className="font-display text-xl sm:text-2xl font-bold tracking-tight">
                                            {title}
                                        </h1>
                                    )}
                                    {helper && (
                                        <p className="text-muted-foreground mt-1.5 text-sm leading-snug">
                                            {helper}
                                        </p>
                                    )}
                                </div>
                            )}

                            {/* Demo creds box — removed pre-soft-launch.
                                Test credentials live in /app/memory/test_credentials.md
                                for internal QA use only. */}

                            <div className="space-y-1.5">
                                <Label htmlFor="phone">Phone number</Label>
                                <PhoneInput10
                                    id="phone"
                                    value={phone}
                                    onChange={setPhone}
                                    placeholder="98765 43210"
                                    inputClassName={(roleKey === "doctor" || roleKey === "patient") ? "h-12 rounded-r-xl bg-white/50 focus:bg-white transition-all focus:ring-2 focus:ring-primary/20 shadow-sm" : "h-11 rounded-r-xl"}
                                    className={(roleKey === "doctor" || roleKey === "patient") ? "h-12" : "h-11"}
                                    testid="phone-input"
                                />
                                <p className="text-xs text-muted-foreground">
                                    Indian mobile, 10 digits. We&apos;ll send a 6-digit OTP to verify.
                                </p>
                            </div>

                            {/* 2026-02-28 — Name field removed from Patient
                                login screen. Patient Portal sign-in is now
                                phone + OTP only (Razorpay/Zomato convention).
                                If the patient is brand new with no name on
                                file, a soft welcome card on the Patient
                                Dashboard captures it just-in-time. Splits
                                "auth" from "profile completion" cleanly. */}

                            {/* Invite gate removed 2026-02-28 — open
                                signup is back. Doctor/COE/admin still
                                invite-only by phone whitelist. */}

                            <Button
                                type="submit"
                                disabled={loading}
                                className={
                                    (roleKey === "doctor" || roleKey === "patient")
                                    ? "w-full h-12 mt-2 rounded-full bg-gradient-to-r from-primary to-[#003E50] hover:from-[#003E50] hover:to-primary text-white shadow-[0_4px_14px_rgba(0,124,157,0.25)] hover:shadow-[0_6px_20px_rgba(0,124,157,0.4)] transition-all hover:-translate-y-0.5 font-semibold tracking-wide"
                                    : "w-full h-11 rounded-full bg-primary hover:bg-primary/90"
                                }
                                data-testid="send-otp-btn"
                            >
                                {loading ? "Sending..." : "Send OTP"}
                            </Button>

                            <p className="text-[11px] text-center text-muted-foreground">
                                By continuing, you agree to our Terms & Privacy Policy.
                            </p>

                            {/* iter98.62 — Doctor-Portal onboarding nudge.
                                Only rendered on /doctor-login. New doctors
                                landing here from the public site shouldn't
                                hit a phone-only dead-end; surface the two
                                onboarding paths inline:
                                  · Talk to sales  → opens DemoRequestDialog
                                                     (POST /api/b2b-leads
                                                     → territory-routed)
                                  · Apply directly → /for-doctors#apply
                                                     (full ApplyWizard)
                                Other portals (admin/coe/sales/patient) are
                                invite-only or already linked from the
                                landing page, so no nudge for them. */}
                            {roleKey === "doctor" && (
                                <div
                                    className="pt-0.5 animate-in fade-in duration-700"
                                    data-testid="doctor-onboarding-nudge"
                                >
                                    <div className="flex items-center gap-3 mb-2">
                                        <div className="flex-1 h-px bg-border" />
                                        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                                            New to Invisique™?
                                        </span>
                                        <div className="flex-1 h-px bg-border" />
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setDemoOpen(true)}
                                            className="group flex flex-col items-start gap-0.5 px-3 py-2 rounded-xl border border-white/60 bg-white/40 hover:border-primary/30 hover:bg-primary/[0.03] hover:shadow-sm transition-all text-left"
                                            data-testid="doctor-onboarding-demo-btn"
                                        >
                                            <span className="w-5 h-5 rounded-md bg-white border border-border/50 text-primary grid place-items-center group-hover:bg-primary group-hover:text-white transition-colors shadow-sm">
                                                <Calendar size={11} weight="fill" />
                                            </span>
                                            <span className="text-[12px] font-semibold text-foreground leading-tight">
                                                Talk to sales
                                            </span>
                                            <span className="text-[10px] text-muted-foreground leading-snug">
                                                Book a demo with our team.
                                            </span>
                                        </button>
                                        <a
                                            href="/for-doctors#apply"
                                            className="group flex flex-col items-start gap-0.5 px-3 py-2 rounded-xl border border-white/60 bg-white/40 hover:border-primary/30 hover:bg-primary/[0.03] hover:shadow-sm transition-all text-left"
                                            data-testid="doctor-onboarding-apply-btn"
                                        >
                                            <span className="w-5 h-5 rounded-md bg-white border border-border/50 text-primary grid place-items-center group-hover:bg-primary group-hover:text-white transition-colors shadow-sm">
                                                <FilePlus size={11} weight="fill" />
                                            </span>
                                            <span className="text-[12px] font-semibold text-foreground leading-tight">
                                                Apply directly
                                                <ArrowRight
                                                    size={11}
                                                    weight="bold"
                                                    className="inline ml-1 -mt-0.5 opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all"
                                                />
                                            </span>
                                            <span className="text-[10px] text-muted-foreground leading-snug">
                                                Submit an application.
                                            </span>
                                        </a>
                                    </div>
                                </div>
                            )}
                        </form>
                    )}

                    {step === 2 && (
                        <form onSubmit={verifyOtp} className="space-y-4 relative z-10 animate-in fade-in slide-in-from-right-4 duration-500" data-testid="otp-form">
                            {/* Inline sign-in error — generic, non-disclosing.
                                Replaces the prior "Wrong portal" amber banner
                                (which leaked which portal a phone belonged to).
                                Same input remains on screen so the user can
                                retry with a corrected phone or OTP. */}
                            {signinError && (
                                <div
                                    className="rounded-xl border border-rose-200 bg-rose-50 p-3 flex items-start gap-3 animate-in fade-in slide-in-from-top-2"
                                    data-testid="signin-error-banner"
                                    role="alert"
                                >
                                    <WarningCircle size={18} weight="fill" className="text-rose-600 shrink-0 mt-0.5" />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-semibold text-rose-900">
                                            {signinError}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => { setSigninError(null); setStep(1); setCode(""); }}
                                            className="mt-1 text-xs font-medium text-rose-800/80 hover:text-rose-900 underline"
                                            data-testid="signin-error-retry"
                                        >
                                            Try a different phone number
                                        </button>
                                    </div>
                                </div>
                            )}

                            {(roleKey === "doctor" || roleKey === "patient") ? (
                                <div className="text-center mb-4">
                                    <div className="inline-flex items-center gap-2 mb-2">
                                        <div className="w-6 h-px bg-border" />
                                        <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-primary">Verification</div>
                                        <div className="w-6 h-px bg-border" />
                                    </div>
                                    <h1 className="font-display text-xl sm:text-2xl font-bold tracking-tight text-foreground">Enter 6-digit code</h1>
                                    <p className="text-muted-foreground mt-1 text-sm">
                                        Sent to <span className="font-medium text-foreground">+91 {phone}</span>
                                    </p>
                                    {devCode && (
                                        <div className="mt-3 p-2.5 bg-primary/10 rounded-xl text-sm text-left" data-testid="dev-otp-notice">
                                            <strong>Dev mode:</strong> Your OTP is <span className="font-mono font-bold">{devCode}</span>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="text-center">
                                    <div className="overline text-primary mb-1">Verification</div>
                                    <h1 className="font-display text-xl sm:text-2xl font-bold tracking-tight">Enter the 6-digit code</h1>
                                    <p className="text-muted-foreground mt-1.5 text-sm">
                                        Sent to <span className="font-medium text-foreground">+91 {phone}</span>
                                    </p>
                                    {devCode && (
                                        <div className="mt-3 p-2.5 bg-primary/10 rounded-xl text-sm text-left" data-testid="dev-otp-notice">
                                            <strong>Dev mode:</strong> Your OTP is <span className="font-mono font-bold">{devCode}</span>
                                        </div>
                                    )}
                                </div>
                            )}
                            <div className="flex justify-center">
                                <InputOTP maxLength={6} value={code} onChange={setCode} data-testid="otp-input">
                                    <InputOTPGroup>
                                        {[0, 1, 2, 3, 4, 5].map((i) => (
                                            <InputOTPSlot
                                                key={i}
                                                index={i}
                                                className={`w-11 h-11 text-lg ${(roleKey === "doctor" || roleKey === "patient") ? "bg-white/60 focus:bg-white transition-colors" : ""}`}
                                                data-testid={`otp-input-${i}`}
                                            />
                                        ))}
                                    </InputOTPGroup>
                                </InputOTP>
                            </div>
                            <Button
                                type="submit"
                                disabled={loading || code.length !== 6}
                                className={
                                    (roleKey === "doctor" || roleKey === "patient")
                                    ? "w-full h-12 rounded-full bg-gradient-to-r from-primary to-[#003E50] hover:from-[#003E50] hover:to-primary text-white shadow-[0_4px_14px_rgba(0,124,157,0.25)] hover:shadow-[0_6px_20px_rgba(0,124,157,0.4)] transition-all hover:-translate-y-0.5 font-semibold tracking-wide"
                                    : "w-full h-11 rounded-full"
                                }
                                data-testid="verify-otp-btn"
                            >
                                {loading ? "Verifying..." : "Verify & continue"}
                            </Button>
                            <div className="text-center text-sm">
                                <button type="button" onClick={() => setStep(1)} className="text-muted-foreground hover:text-primary transition-colors">
                                    Change phone number
                                </button>
                            </div>
                        </form>
                    )}
                </div>
                </div>
            </div>

            {/* iter98.62 — Sales-led onboarding modal. Mounted at the
                root so its overlay sits above the login card. Only the
                "Talk to sales" CTA on /doctor-login can open it. */}
            <DemoRequestDialog open={demoOpen} onOpenChange={setDemoOpen} />
        </div>
    );
}
